import React from "react";
import { useTranslator } from "@brickme/frontend-common/src/i18n/translation.tsx";
import Modal from "~/components/modal.tsx";
import useAppDispatch from "~/hooks/use-app-dispatch.ts";
import useAppSelector from "~/hooks/use-app-selector.ts";
import LoginOrSignUpForm from "../../nav/login-or-sign-up-form.tsx";
import NameForm, { type NameValues } from "../../nav/name-form.tsx";
import { setGiftRedemptionStep } from "../store-slice.ts";
import {
	setParsedNameValues,
	guestLogin,
} from "~/features/user/store-slice.ts";
import { selectUser } from "~/features/user/selectors.ts";

type GiftAuthFormProps = {
	readonly show: boolean;
};

function GiftAuthForm({ show }: GiftAuthFormProps) {
	const t = useTranslator();
	const dispatch = useAppDispatch();
	const user = useAppSelector(selectUser);
	const parsedNameValues = useAppSelector(
		(state) => state.user.parsedNameValues,
	);

	// Don't show if user is already signed in
	if (user.type === "signedIn") {
		throw new Error("GiftAuthForm: Invalid state - user is already signed in.");
	}

	// Determine current step based on Redux state (like get-started flow)
	const isOtpFlow =
		user.type === "waitingForOtp" || user.type === "verifyingOtp";
	const showEmailForm = parsedNameValues || isOtpFlow;

	const handleNameSubmit = (nameValues: NameValues): Promise<void> => {
		// Store parsed name values in Redux for later use
		dispatch(setParsedNameValues(nameValues));
		return Promise.resolve();
	};

	const handleEmailSuccess = () => {
		dispatch(
			setGiftRedemptionStep({
				type: "configuring",
				giftFramingVariantId: undefined,
				giftFramingVariantQty: undefined,
			}),
		);
		// Continue with normal app flow after successful auth
	};

	const handleGuestLogin = () => {
		// Set user to guest state (this will make the guest sign-in control appear in header)
		dispatch(guestLogin());

		dispatch(
			setGiftRedemptionStep({
				type: "configuring",
				giftFramingVariantId: undefined,
				giftFramingVariantQty: undefined,
			}),
		);
		// Continue with normal app flow after guest login
	};

	const previouslyCollectedName = parsedNameValues
		? `${parsedNameValues.firstName} ${parsedNameValues.middleName || ""} ${parsedNameValues.lastName || ""}`.trim()
		: undefined;

	return (
		<Modal title="" show={show} slimBorder>
			{!showEmailForm ? (
				<div className="get-started-step">
					<div className="get-started-content">
						<h1 className="get-started-title">{t("Let's get to know you")}</h1>

						<NameForm
							isWorking={false}
							submitLabel={t("Continue")}
							onSubmit={handleNameSubmit}
						/>
					</div>
				</div>
			) : (
				<div className="get-started-step">
					<div className="get-started-content">
						<h1 className="get-started-title">
							{isOtpFlow
								? t("Enter verification code")
								: previouslyCollectedName
									? t("Great to meet you, {name}!", {
											name: previouslyCollectedName,
										})
									: t("Great to meet you!")}
						</h1>
						{!isOtpFlow && (
							<p className="get-started-subtitle">
								{t("We'll use your email to save your progress.")}
							</p>
						)}

						<LoginOrSignUpForm
							showJustSignedUpMessage={true}
							finalSubmitLabel={t("Continue")}
							previouslyCollectedName={previouslyCollectedName}
							onSuccess={handleEmailSuccess}
						>
							<div className="guest-option">
								<button
									className="guest-button"
									onClick={handleGuestLogin}
									type="button"
								>
									{t("Continue without an account")}
								</button>
							</div>
						</LoginOrSignUpForm>
					</div>
				</div>
			)}
		</Modal>
	);
}

export default GiftAuthForm;
